import { apiFetch } from "@/lib/api";

/** Standard Django envelope; list endpoints add `meta`. */
interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

// --- Tag groups (categories) ---------------------------------------------

export interface TagGroup {
  id: string;
  name: string;
  position: number;
}

interface ApiTagGroup {
  id: string;
  name: string;
  position: number;
}

function toTagGroup(g: ApiTagGroup): TagGroup {
  return { id: g.id, name: g.name, position: g.position };
}

export async function listTagGroups(): Promise<TagGroup[]> {
  const { data } = await apiFetch<Envelope<ApiTagGroup[]>>(
    "/firm/tag-groups/?page_size=100",
  );
  return data.map(toTagGroup);
}

export async function createTagGroup(name: string): Promise<TagGroup> {
  const { data } = await apiFetch<Envelope<ApiTagGroup>>("/firm/tag-groups/", {
    method: "POST",
    json: { name },
  });
  return toTagGroup(data);
}

export async function renameTagGroup(id: string, name: string): Promise<TagGroup> {
  const { data } = await apiFetch<Envelope<ApiTagGroup>>(`/firm/tag-groups/${id}/`, {
    method: "PATCH",
    json: { name },
  });
  return toTagGroup(data);
}

/** DELETE /firm/tag-groups/:id/ — 400 with `{detail}` when the group still has tags. */
export function deleteTagGroup(id: string): Promise<void> {
  return apiFetch<void>(`/firm/tag-groups/${id}/`, { method: "DELETE" });
}

// --- Tags -----------------------------------------------------------------

export interface Tag {
  id: string;
  groupId: string;
  groupName: string;
  name: string;
  position: number;
  isArchived: boolean;
  /** Null on the list endpoint; populated by `getTag`. */
  clientCount: number | null;
}

interface ApiTag {
  id: string;
  group: string;
  group_name: string;
  name: string;
  position: number;
  is_archived: boolean;
  client_count: number | null;
}

function toTag(t: ApiTag): Tag {
  return {
    id: t.id,
    groupId: t.group,
    groupName: t.group_name,
    name: t.name,
    position: t.position,
    isArchived: t.is_archived,
    clientCount: t.client_count,
  };
}

export async function listTags(): Promise<Tag[]> {
  const { data } = await apiFetch<Envelope<ApiTag[]>>("/firm/tags/?page_size=100");
  return data.map(toTag);
}

/** GET /firm/tags/:id/ — same shape as the list, but with a real `clientCount`. */
export async function getTag(id: string): Promise<Tag> {
  const { data } = await apiFetch<Envelope<ApiTag>>(`/firm/tags/${id}/`);
  return toTag(data);
}

export async function createTag(groupId: string, name: string): Promise<Tag> {
  const { data } = await apiFetch<Envelope<ApiTag>>("/firm/tags/", {
    method: "POST",
    json: { group: groupId, name },
  });
  return toTag(data);
}

export async function updateTag(
  id: string,
  patch: { name?: string; groupId?: string },
): Promise<Tag> {
  const json: Record<string, unknown> = {};
  if (patch.name !== undefined) json.name = patch.name;
  if (patch.groupId !== undefined) json.group = patch.groupId;
  const { data } = await apiFetch<Envelope<ApiTag>>(`/firm/tags/${id}/`, {
    method: "PATCH",
    json,
  });
  return toTag(data);
}

export function deleteTag(id: string): Promise<void> {
  return apiFetch<void>(`/firm/tags/${id}/`, { method: "DELETE" });
}

/** POST /firm/tags/:id/archive/ — toggles archived; returns the updated tag. */
export async function archiveTag(id: string): Promise<Tag> {
  const { data } = await apiFetch<Envelope<ApiTag>>(`/firm/tags/${id}/archive/`, {
    method: "POST",
  });
  return toTag(data);
}

/** POST /firm/tags/reorder/ — persist a new order; returns the full tag list. */
export async function reorderTags(ids: string[]): Promise<Tag[]> {
  const { data } = await apiFetch<Envelope<ApiTag[]>>("/firm/tags/reorder/", {
    method: "POST",
    json: { ids },
  });
  return data.map(toTag);
}
